import React, { useCallback, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Button,
  TextField,
  Stack,
} from '@mui/material';
import Refresh from '@mui/icons-material/Refresh';
import Email from '@mui/icons-material/Email';
import TrendingUp from '@mui/icons-material/TrendingUp';
import { toast } from 'react-toastify';
import {
  getRevenueCashflowReport,
  sendRevenueCashflowReportEmail,
  RevenueCashflowReportResponse,
  CustomerRevenueCashflowRow,
} from '../../../services/request/Reports';
import { formatCurrency } from '../../../utils/CurrencyFormat';
import { PageHeader, DataToolbar, EmptyState } from '../../../shared-components/ui';

const RevenueCashflowReport: React.FC = () => {
  const [data, setData] = useState<RevenueCashflowReportResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [email, setEmail] = useState('');

  const fetchReport = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getRevenueCashflowReport();
      const payload = response.data;
      if (payload) {
        setData(payload);
      } else {
        setError('No data returned');
      }
    } catch (e: any) {
      setError(e?.message || 'Failed to load revenue/cashflow report.');
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const handleSendEmail = async () => {
    try {
      setSendingEmail(true);
      const res = await sendRevenueCashflowReportEmail(email || undefined);
      const msg = res?.data?.message || res?.message || 'Report sent successfully.';
      toast.success(msg);
    } catch (e: any) {
      toast.error(e?.message || 'Failed to send report email.');
    } finally {
      setSendingEmail(false);
    }
  };

  const formatAmount = (value: number) => formatCurrency(value, 'INR', true);

  return (
    <Box>
      <PageHeader
        eyebrow="Lending · Reports"
        title="Revenue & cashflow by customer"
        subtitle="Revenue = Interest + Late Fees. Net cashflow = Cash inflow − Disbursed."
        actions={
          <Button variant="contained" startIcon={<Refresh />} onClick={fetchReport} disabled={loading}>
            {loading ? 'Loading…' : 'Refresh'}
          </Button>
        }
      />

      <DataToolbar>
        <TextField
          size="small"
          label="Send report to email (optional)"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Leave empty for default report email"
          sx={{ minWidth: 280 }}
        />
        <Button
          variant="contained"
          color="primary"
          startIcon={<Email />}
          onClick={handleSendEmail}
          disabled={sendingEmail}
        >
          {sendingEmail ? 'Sending…' : 'Send report to email'}
        </Button>
      </DataToolbar>

      {loading && <CircularProgress size={24} sx={{ mb: 2 }} />}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {data && (
        <>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
            <Card sx={{ minWidth: 180 }}>
              <CardContent>
                <Typography color="text.secondary" gutterBottom variant="body2">
                  Total Customers
                </Typography>
                <Typography variant="h6">{data.totalCustomers}</Typography>
              </CardContent>
            </Card>
            <Card sx={{ minWidth: 180 }}>
              <CardContent>
                <Typography color="text.secondary" gutterBottom variant="body2">
                  Total Revenue
                </Typography>
                <Typography variant="h6">{formatAmount(data.totalRevenue)}</Typography>
              </CardContent>
            </Card>
            <Card sx={{ minWidth: 180 }}>
              <CardContent>
                <Typography color="text.secondary" gutterBottom variant="body2">
                  Total Net Cashflow
                </Typography>
                <Typography variant="h6">{formatAmount(data.totalNetCashflow)}</Typography>
              </CardContent>
            </Card>
          </Stack>

          <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>#</TableCell>
                  <TableCell>Customer ID</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell align="right">Interest (₹)</TableCell>
                  <TableCell align="right">Late Fees (₹)</TableCell>
                  <TableCell align="right">Revenue (₹)</TableCell>
                  <TableCell align="right">Principal (₹)</TableCell>
                  <TableCell align="right">Disbursed (₹)</TableCell>
                  <TableCell align="right">Cash Inflow (₹)</TableCell>
                  <TableCell align="right">Net Cashflow (₹)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.rows.map((r: CustomerRevenueCashflowRow, idx: number) => (
                  <TableRow key={r.borrowerId}>
                    <TableCell>{idx + 1}</TableCell>
                    <TableCell>{r.customerId || '—'}</TableCell>
                    <TableCell>{r.borrowerName || '—'}</TableCell>
                    <TableCell align="right">{formatAmount(r.interestCollected)}</TableCell>
                    <TableCell align="right">{formatAmount(r.lateFeesCollected)}</TableCell>
                    <TableCell align="right">{formatAmount(r.revenue)}</TableCell>
                    <TableCell align="right">{formatAmount(r.principalCollected)}</TableCell>
                    <TableCell align="right">{formatAmount(r.totalDisbursed)}</TableCell>
                    <TableCell align="right">{formatAmount(r.cashInflow)}</TableCell>
                    <TableCell align="right">{formatAmount(r.netCashflow)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {!loading && !error && data && data.rows.length === 0 && (
        <EmptyState title="No customer data" description="No revenue or cashflow data available for customers." />
      )}
    </Box>
  );
};

export default RevenueCashflowReport;
